/**
 * Lucide icon per category: explicit slug map → ordered Hebrew/EN keyword rules → hash fallback.
 */
import {
  Apple,
  Baby,
  Banana,
  Bath,
  Bean,
  Beef,
  Beer,
  Candy,
  Carrot,
  Cat,
  Cherry,
  ChefHat,
  CircleDot,
  Coffee,
  Cookie,
  Croissant,
  CupSoda,
  Dog,
  Droplets,
  Drumstick,
  Egg,
  Fish,
  Flower2,
  Gift,
  GlassWater,
  Grape,
  Heart,
  Home,
  IceCream,
  Milk,
  Package,
  Pill,
  Refrigerator,
  Sandwich,
  ShoppingBasket,
  Snowflake,
  Soup,
  Sparkles,
  Shirt,
  Store,
  UtensilsCrossed,
  Wheat,
  Wine,
} from "lucide-react";

/** Exact slug (lowercase) → icon. Extend when API slugs are stable. */
const SLUG_ICON_MAP = {
  beverages: GlassWater,
  drinks: GlassWater,
  wine: Wine,
  wines: Wine,
  beer: Beer,
  beers: Beer,
  dairy: Milk,
  milk: Milk,
  meat: Beef,
  beef: Beef,
  chicken: Drumstick,
  poultry: Drumstick,
  turkey: Drumstick,
  fish: Fish,
  seafood: Fish,
  fruits: Banana,
  vegetables: Carrot,
  produce: Carrot,
  bakery: Croissant,
  bread: Croissant,
  pastry: Croissant,
  frozen: Snowflake,
  cleaning: Sparkles,
  household: Sparkles,
  hygiene: Bath,
  "personal-care": Bath,
  toiletry: Bath,
  toiletries: Bath,
  snacks: Candy,
  sweets: Candy,
  candy: Candy,
  cereal: Wheat,
  pasta: Wheat,
  rice: Wheat,
  flour: Wheat,
  eggs: Egg,
  baby: Baby,
  pharmacy: Pill,
  health: Pill,
  deli: Sandwich,
  delicatessen: Sandwich,
  spices: ChefHat,
  oils: Droplets,
  oil: Droplets,
  nuts: Bean,
  legumes: Bean,
  cans: Soup,
  canned: Soup,
  general: Home,
  home: Home,
  organic: Apple,
  gifts: Gift,
  flowers: Flower2,
  pet: Dog,
  pets: Dog,
  dog: Dog,
  cat: Cat,
  water: GlassWater,
  coffee: Coffee,
  tea: Coffee,
  juice: Grape,
  frozenfood: Snowflake,
  "frozen-food": Snowflake,
  chilled: Refrigerator,
  refrigerated: Refrigerator,
  cooling: Refrigerator,
  mekorarim: Refrigerator,
};

/**
 * More specific patterns first. Avoid bare "ים" (matches too many Hebrew words).
 */
const RULES = [
  // — Beverages (split: wine / beer / soda / water / generic)
  [/יין|יינות|^wine$|-wine|\/wine/i, Wine],
  [/בירה|beer/i, Beer],
  [
    /מוגז|קולה|ספרייט|פפסי|מוגזים|energy\s*drink|soda|soft\s*-?\s*drink/i,
    CupSoda,
  ],
  [
    /מים|מינרל|שתיית\s*מים|מי\s*שתייה|מאבקה|mineral\s*water|water(?!\s*melon)/i,
    GlassWater,
  ],
  [/מיץ|נקטר|juice|nectar/i, Grape],
  [/משקאות|שתייה|beverage|beverages|drinks(?!-only)/i, GlassWater],

  // — Fresh
  [/פירות|פרי|פירות\s*טריים|fruit(?!-juice)/i, Apple],
  [/ירק|ירקות|חסה|עגב|בצל|תפו"?ח|vegetable|salad|carrot/i, Carrot],
  [/בננה|banana/i, Banana],

  // — Protein
  [/עוף|הודו|כנפ|chicken|poultry|drumstick|turkey(?!-smoke)/i, Drumstick],
  [/בשר|meat|טלה|כבש|veal|lamb(?!-chop)/i, Beef],
  [/דגים|דגי|סלמון|טונה|מאכלי\s*ים|fish|seafood|tuna|salmon|anchov/i, Fish],

  // — Dairy & alternatives
  [/חלב|מחלבה|dairy|מחלבות|גבינ|יוגורט|קוטג|cream\s*cheese|mozzarella/i, Milk],

  // — Dry / shelf-stable
  [/דגנים|דייסה|פירורי\s*לחם|cereal|granola|musli/i, Wheat],
  [/פסטה|אורז|קוסקוס|bulgur|נודלס|pasta|rice(?!-cake)|couscous/i, Wheat],
  [/קטניות|עדש|חומוס|שעועית|beans?\s*&?\s*legumes/i, Bean],
  [/קמח|שמרים|אפייה|mix\s*baking/i, Croissant],
  [/שימורים|קופסאות|קופס|מרק\s*מוכן|canned|preserve/i, Soup],
  [/עוג|עוגיות|ביסקוויט|וופל|קראנץ|cookie|biscuit|wafer/i, Cookie],

  // — Chilled (מקרר / קירור) — not deep-frozen; distinct from snowflake “קפוא”
  [
    /מקוררים?|מצוננים?|מוצרי\s*קירור|קירור\s*(?!עמוק)|צ'?ילד|chilled|refrigerated/i,
    Refrigerator,
  ],

  // — Frozen / cold
  [/קפוא|קירור\s*עמוק|גליד|frozen|ice\s*cream|gelato/i, Snowflake],
  [/קינוח|קינוחים|גלידריה/i, IceCream],

  // — Bakery
  [/אפייה|לחם|מאפ|לחמנ|באגט|מאפה|מאפים|bread|baguette|roll(?!-s)/i, Croissant],

  // — Hot drinks
  [
    /קפה|אספרסו|נסpresso|תה|חליט|צמחי|coffee|espresso|chai|matcha|tea(?!-spoon)/i,
    Coffee,
  ],

  // — Hygiene & home
  [
    /ניקיון|חומרי\s*ניקוי|סבון|אקונומיקה|detergent|cleaning|laundry(?!-deter)/i,
    Sparkles,
  ],
  [
    /היגיינה|טואלטיקה|טיפוח|קוסמטיקה|שמפו|מרכך|דאודורנט|משחת\s*שיניים|מברשת\s*שיניים|אלכוג'ל|קרם\s*ידיים|קרם\s*גוף|toiletr|personal\s*care|hygien|shampoo|conditioner|deodorant|tooth\s*paste|toothpaste/i,
    Bath,
  ],
  [/נר|מפ|עיתון|paper(?!-clip)/i, Package],

  // — Health & personal
  [/תרופ|pharm|ויטמין|אבקה|supplement|צמחי\s*מרפא|הומאופתי/i, Pill],
  [/תינוק|תינוקות|חיתול|מזון\s*תינוק|baby(?!-oil)/i, Baby],
  [/ביגוד|מגבת|טקסטיל|textile|underwear(?!-set)/i, Shirt],

  // — Specialty
  [/תבלין|בישול|צלי|תיבול|מרינד|spice|seasoning|stock\s*cube/i, ChefHat],
  [/שמן|חומץ|רטבים|ממרח\s*שום|oil(?!\s*painting)|vinegar|dressing/i, Droplets],
  [/מעדנייה|גורמה|גבינות\s*מובחרות|חזק\s*מזין|deli|charcuterie/i, Sandwich],

  // — Treats
  [/חטיף|ממתק|שוקולד|טופי|candy|snack|chocolate|sweet(?!\s*potato)/i, Candy],

  // — Eggs
  [/ביצים|ביצה(?!\s*חומה)|egg(?!-plant)/i, Egg],

  // — Misc literal categories
  [/פרח|flower|פרחים/i, Flower2],
  [/כלב|כלבים|dog\s*food/i, Dog],
  [/חתול|cats?\s*food/i, Cat],
  [/מתנה|עיצוב\s*בית|gift\s*wrap/i, Gift],
  [/בית|כללי|מגוון/i, Home],
];

const FALLBACK_ICONS = [
  ShoppingBasket,
  Cherry,
  Grape,
  Refrigerator,
  IceCream,
  Soup,
  Sandwich,
  UtensilsCrossed,
  Heart,
  CircleDot,
  Candy,
  Wheat,
  Egg,
  Sparkles,
  Fish,
  Coffee,
  Croissant,
  Store,
];

function stableHash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function normalizeSlug(slug) {
  return String(slug || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "");
}

function iconFromSlugMap(slug) {
  const norm = normalizeSlug(slug);
  if (!norm) return null;
  if (SLUG_ICON_MAP[norm]) return SLUG_ICON_MAP[norm];
  const segments = norm.split(/[/\-_]+/).filter(Boolean);
  for (const seg of segments) {
    if (SLUG_ICON_MAP[seg]) return SLUG_ICON_MAP[seg];
  }
  return null;
}

/**
 * @returns {import("lucide-react").LucideIcon}
 */
export function getCategoryNavIconComponent(slug = "", name = "", index = 0) {
  const mapped = iconFromSlugMap(slug);
  if (mapped) return mapped;

  const blob = `${slug} ${name}`;
  for (const [re, Icon] of RULES) {
    if (re.test(blob)) return Icon;
  }

  const key = `${slug || "x"}|${name || ""}|${index}`;
  return FALLBACK_ICONS[stableHash(key) % FALLBACK_ICONS.length];
}
